import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';

// Import reusable components
import { BarChartComponent } from '../../../charts/bar-chart/bar-chart.component';
import { PolarChartComponent } from '../../../charts/polar-chart/polar-chart.component';
import { PieGridComponent } from '../../../charts/pie-grid/pie-grid.component';
import { TableComponent, TableColumn } from '../../table/table.component';
import { ScoreCardsComponent } from '../../score-cards/score-cards.component';
import { EvidenceAndRecommendationsComponent } from '../../evidence-and-recommendations/evidence-and-recommendations.component';

@Component({
  selector: 'app-common-methodology',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatListModule,
    MatDividerModule,
    BarChartComponent,
    // PolarChartComponent,
    // PieGridComponent,
    TableComponent,
    ScoreCardsComponent,
    EvidenceAndRecommendationsComponent
  ],
  templateUrl: './common-methodology.component.html',
  styleUrls: ['./common-methodology.component.scss']
})
export class CommonMethodologyComponent implements OnInit, OnChanges {
  @Input() data: any = null;
  @Input() universities: string[] = [];
  @Input() title: string = 'Our Methodology';
  @Input() evidenceSources: any[] = [];
  @Input() recommendations: any[] = [];

  // Chart data
  methodologyRadarData: any[] = [];
  methodologyBarData: any[] = [];
  methodologyPieData: any[] = [];

  // Table data
  methodologyTableData: any[] = [];
  methodologyTableColumns: TableColumn[] = [
    { key: 'component', label: 'Methodology Component', type: 'text' },
    { key: 'weight', label: 'Weight (%)', type: 'percentage' },
    { key: 'reliability_score', label: 'Reliability Score', type: 'percentage' },
    { key: 'description', label: 'Description', type: 'text' }
  ];

  // Score cards data
  overallScores: any[] = [];

  // Enhanced API response data
  overallMethodology: any = null;
  dataQualityMetrics: any = null;
  methodologyComponents: any[] = [];


  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.processData();
    }
  }

  private processData(): void {
    if (!this.data) return;

    // Handle both direct data and transformed data from service
    const responseData = this.data.methodology_data || this.data;

    // Extract evidence sources and recommendations
    this.evidenceSources = responseData.evidence_sources || [];
    this.recommendations = responseData.recommendations || [];

    // Process methodology data from the new API structure
    if (responseData.data && Array.isArray(responseData.data)) {
      this.processMethodologyData(responseData.data);
    }

    // Process methodology components
    if (responseData.methodology_components && Array.isArray(responseData.methodology_components)) {
      this.processMethodologyComponents(responseData.methodology_components);
    }

    // Process overall methodology
    if (responseData.overall_methodology) {
      this.processOverallMethodology(responseData.overall_methodology);
    }

    // Process data quality metrics
    if (responseData.data_quality_metrics) {
      this.processDataQualityMetrics(responseData.data_quality_metrics);
    }
  }

  private processMethodologyData(data: any[]): void {
    // Create methodology radar chart data
    this.methodologyRadarData = data.map(item => ({
      name: item.name,
      series: item.series || []
    }));

    // Create methodology bar chart data
    this.methodologyBarData = data.map(item => ({
      name: item.name,
      series: item.series || []
    }));

    // Create methodology pie chart data
    this.methodologyPieData = data.map(item => ({
      name: item.name,
      value: item.series ? item.series.reduce((sum: number, s: any) => sum + s.value, 0) / item.series.length : 0
    }));

    // Calculate overall scores for score cards
    this.overallScores = data.map(item => {
      const avgScore = item.series ?
        item.series.reduce((sum: number, s: any) => sum + s.value, 0) / item.series.length : 0;

      return {
        name: item.name,
        'Methodology Score': avgScore,
        value: avgScore,
        label: 'Methodology Score'
      };
    });
  }

  private processMethodologyComponents(components: any[]): void {
    this.methodologyComponents = components;

    // Create table data
    this.methodologyTableData = components.map(comp => ({
      component: comp.component,
      weight: comp.weight,
      reliability_score: comp.reliability_score,
      description: comp.description
    }));
  }

  private processOverallMethodology(methodology: any): void {
    this.overallMethodology = methodology;
  }

  private processDataQualityMetrics(metrics: any): void {
    this.dataQualityMetrics = metrics;
  }

  // Helper methods for conditional rendering
  hasMethodologyData(): boolean {
    return this.methodologyRadarData.length > 0;
  }

  hasMethodologyComponents(): boolean {
    return this.methodologyComponents.length > 0;
  }

  hasOverallMethodology(): boolean {
    return this.overallMethodology !== null;
  }

  hasDataQualityMetrics(): boolean {
    return this.dataQualityMetrics !== null;
  }

  hasEvidenceSources(): boolean {
    return this.evidenceSources.length > 0;
  }

  hasRecommendations(): boolean {
    return this.recommendations.length > 0;
  }

  getMethodologyTableColumns(): string[] {
    return this.methodologyTableColumns.map(col => col.key);
  }

}
